import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import debounce from "lodash.debounce";
import {forwardRef, useEffect, useRef, useState} from "react";

import {LabeledTextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    LabelSmall,
    type Typography,
} from "@khanacademy/wonder-blocks-typography";
import {useGql} from "@khan/data-gql";

import {CHECK_USERNAME_QUERY} from "./check-username-query.ts";
import {
    getUsernameValidationString,
    USERNAME_MAX_LENGTH,
    USERNAME_MIN_LENGTH,
} from "./password-util.ts";

import infoIcon from "@phosphor-icons/core/regular/info.svg";

export interface CommonUsernameTextFieldProps {
    /**
     * Provide a label for the field.
     */
    label: string | React.ReactElement<React.ComponentProps<Typography>>;
    /**
     * Provide a description for the field.
     */
    description?: string | React.ReactElement<React.ComponentProps<Typography>>;
    /**
     * Called when the value has changed.
     */
    onChange: (newValue: string) => unknown;
    /**
     * Called when the field input is validated.
     *
     * The extra `value` parameter is added to provide the latest
     * state of `value` due to UsernameTextField's asynchronous behavior.
     */
    onValidate?: (
        errorMessage: string | null | undefined,
        value: string,
    ) => unknown;
    /**
     * Provide a placeholder for the input field.
     */
    placeholder?: string;
    /**
     * Provide a different empty error message than the default.
     */
    emptyMessage?: string;
    /**
     * Disable the input field.
     */
    disabled?: boolean;
    /**
     * Optional test id for e2e testing.
     * The testId for the internal input element will
     * have "-field" automatically appended.
     */
    testId?: string;
    /**
     * An optional unique identifier for the TextField.
     * If no id is specified, a unique id will be auto-generated.
     */
    id?: string;
    required?: boolean | string;
    tooltip?: string | React.ReactElement;
}

type ControlledProps = CommonUsernameTextFieldProps & {
    /**
     * Value for the field.
     */
    value: string;
};

export const UsernameTextField = forwardRef<HTMLInputElement, ControlledProps>(
    (props, ref) => {
        const gqlFetch = useGql();

        const {
            label,
            description,
            placeholder,
            disabled = false,
            testId,
            id,
            value = "",
            onChange,
            onValidate,
            emptyMessage = "",
            required,
            tooltip,
        } = props;

        const [usernameError, setUsernameError] = useState<
            string | null | undefined
        >(null);

        const [isMounted, setIsMounted] = useState(false);

        const initialValueRef = useRef(value);

        useEffect(() => {
            setIsMounted(true);

            return () => {
                setIsMounted(false);
            };
        }, []);

        useEffect(() => {
            if (!isMounted) {
                return;
            }

            if (usernameError) {
                setUsernameError(null);
            }

            if (!value.length) {
                return handleSetUsernameError(
                    emptyMessage || t`Please choose a username.`,
                );
            }

            if (value.length < USERNAME_MIN_LENGTH) {
                return handleSetUsernameError(t`Too short.`);
            }

            if (value.length > USERNAME_MAX_LENGTH) {
                return handleSetUsernameError(t`Too long.`);
            }

            if (value !== initialValueRef.current) {
                debouncedCheckUsername(value);
            }

            return () => {
                debouncedCheckUsername.cancel();
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this effect when the value changes
        }, [value]);

        const debouncedCheckUsername = debounce(async (debouncedValue) => {
            if (!debouncedValue) {
                return;
            }

            const {checkUsername} = await gqlFetch(CHECK_USERNAME_QUERY, {
                variables: {username: debouncedValue},
            });

            if (checkUsername?.error?.code) {
                const errorMessage = getUsernameValidationString(
                    checkUsername.error.code,
                );
                handleSetUsernameError(errorMessage);
            } else {
                setUsernameError(null);
                onValidate?.(null, debouncedValue);
            }
        }, 250);

        const handleSetUsernameError = (errorMessage: string | null) => {
            setUsernameError(errorMessage);
            onValidate?.(errorMessage, value);
        };

        return (
            <div className={css(styles.wrapper)}>
                {tooltip && (
                    <Tooltip content={tooltip}>
                        {/* Wrapper div is needed since you cannot apply "outline" to
                        a tooltip */}
                        <div className={css(styles.infoIcon)}>
                            <PhosphorIcon
                                icon={infoIcon}
                                color={color.fadedOffBlack64}
                                size="small"
                                aria-label={t`More Info`}
                            />
                        </div>
                    </Tooltip>
                )}
                <LabeledTextField
                    id={id}
                    testId={testId}
                    label={label}
                    description={description}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    ref={ref}
                    style={{overflow: "hidden"}}
                    required={required}
                />
                {usernameError && (
                    <>
                        <Strut size={spacing.small_12} />
                        <LabelSmall style={styles.error}>
                            {usernameError}
                        </LabelSmall>
                    </>
                )}
            </div>
        );
    },
);

const styles = StyleSheet.create({
    error: {
        color: color.red,
    },
    wrapper: {
        position: "relative",
    },
    infoIcon: {
        cursor: "pointer",

        // Position the icon in the top right corner of the text field
        position: "absolute",
        top: 0,
        insetInlineEnd: 0,

        // Needed to have the icon above the label,
        zIndex: 1,

        ":focus": {
            outline: `${color.blue} auto`,
        },
    },
});
